// Overview tab — KPI grid + 30-day query volume trend.
//
// Mirrors web/scripts/admin.js's renderOverview() — same KPIs, same
// computations. The "all KPIs in one big grid" layout matches the
// legacy admin.html (a top row of headline numbers, a small chart card,
// then the per-tab sub-KPIs further down). For the v2 port we just keep
// the headline + trend chart in this tab; per-tab sub-KPIs live with
// their tabs (Activity, Sessions, Users, Datasources).

import { useEffect, useState } from "react";
import type { OrgScope, OverviewResponse } from "../../api/admin";
import { adminApi } from "../../api/admin";
import {
  Chart,
  ErrorBlock,
  KpiCard,
  LoadingBlock,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatSeconds,
  lineTrendOption,
} from "./_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
}

export function OverviewTab({ bearer, scope }: Props) {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    adminApi
      .overview(bearer, scope)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  if (error) return <ErrorBlock error={error} />;
  if (!data) return <LoadingBlock />;

  const q = data.queries || {};
  const qk = data.quality_kpis || {};
  const ck = data.cost_kpis || {};
  const volume = data.query_volume_by_day || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label="Queries (30d)" value={formatNumber(q.last_30d)} />
        <KpiCard label="Queries (7d)" value={formatNumber(q.last_7d)} />
        <KpiCard label="Queries (today)" value={formatNumber(q.today)} />
        <KpiCard label="Active Users (7d)" value={formatNumber(data.active_users_7d)} />
        <KpiCard label="Cache Hit Rate (30d)" value={formatPercent(data.cache_hit_rate_30d)} />
        <KpiCard label="Avg Response (30d)" value={formatSeconds(data.avg_response_time_ms_30d)} />
        <KpiCard
          label="Avg Quality (30d)"
          value={qk.avg_overall_30d != null ? `${(qk.avg_overall_30d * 100).toFixed(0)}%` : "—"}
        />
        <KpiCard label="Avg Cost / Query (30d)" value={formatCurrency(ck.avg_cost_per_query_30d)} />
        <KpiCard
          label="Avg Tokens / Query (30d)"
          value={
            ck.avg_tokens_per_query_30d != null
              ? Math.round(ck.avg_tokens_per_query_30d).toLocaleString()
              : "—"
          }
        />
      </div>

      <div className="rounded-[14px] border border-line bg-white p-4">
        <div className="text-sm font-medium text-neutral-900 mb-2">Query volume (last 30 days)</div>
        {volume.length > 0 ? (
          <Chart option={lineTrendOption(volume)} height={260} />
        ) : (
          <div className="text-sm text-neutral-500 py-12 text-center">No query activity yet</div>
        )}
      </div>
    </div>
  );
}
